import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import qualityMetricRqServiceApi, {
  type CreateQualityMetricPayload,
  type QualityMetric,
  type MetricCasesPage,
  type MetricUserTrends,
  type MetricUsersPage,
  type QualityMetricCase,
  type QualityMetricOrganizationConfiguration,
  type QualityMetricWithUser,
  type UnmatchedProvider,
  type UpdateQualityMetricArgs,
} from '../api/quality-metric-rq.service.api';
import { type QueryOpts } from '../api/query-client';
import { queryKeys } from '../api/query-client-registry';
import { MAX_COMPARED_USERS } from '../constants/quality-metric.constants';
import { getOrganizationId } from '../store/selectors/organization.selector';

/**
 * React-query hooks for managing quality-metric definitions (the org-wide
 * management view). A user's own metrics are a different domain — see
 * `useMyQualityMetrics` in `knowledge-dashboard.hooks`.
 *
 * Mutations invalidate the whole domain rather than patching the cache: the
 * list is sorted by `priority` server-side and an update recomputes group
 * counts and trends, so a refetch is simpler than reproducing that here.
 */

/**
 * The clinician detail read: one metric plus the caller's own user metric.
 * Separate from `qualityMetricsKeys` below, which scopes the management
 * surface — different endpoint, different shape, different consumers.
 */
export const qualityMetricKeys = {
  all: ['qualityMetric'] as const,
  detail: (id: string | null | undefined) => [...qualityMetricKeys.all, 'detail', id] as const,
};

export function useQualityMetric(
  id: string | null | undefined,
  options: QueryOpts<QualityMetricWithUser> = {},
) {
  return useQuery<QualityMetricWithUser>({
    queryKey: qualityMetricKeys.detail(id),
    queryFn: ({ signal }) =>
      qualityMetricRqServiceApi.getQualityMetric({ id: id as string, signal }),
    enabled: Boolean(id),
    ...options,
  });
}

export const qualityMetricsKeys = {
  all: queryKeys.qualityMetricsAll,
  orgList: (includeHidden = false) =>
    [...qualityMetricsKeys.all, 'orgList', includeHidden] as const,
  case: (caseId: string | null | undefined) => [...qualityMetricsKeys.all, 'case', caseId] as const,
  users: (id: string | null | undefined, page: number, pageSize: number) =>
    [...qualityMetricsKeys.all, 'users', id, page, pageSize] as const,
  // Ids are sorted here so re-picking the same people in a different order is
  // a cache hit. Normalisation belongs to the key, not to each caller.
  usersTrends: (id: string | null | undefined, userIds: string[]) =>
    [...qualityMetricsKeys.all, 'usersTrends', id, [...userIds].sort()] as const,
  userCases: (id: string | null | undefined, userId: string | null | undefined) =>
    [...qualityMetricsKeys.all, 'userCases', id, userId] as const,
  unmatchedProviders: () => [...qualityMetricsKeys.all, 'unmatchedProviders'] as const,
};

/**
 * Metrics in the organization, priority-ordered. `includeHidden` is Root only —
 * the backend answers `403` for anyone else, so callers must already be gated.
 */
export function useOrganizationQualityMetrics(
  { includeHidden = false }: { includeHidden?: boolean } = {},
  options: QueryOpts<QualityMetric[]> = {},
) {
  return useQuery<QualityMetric[]>({
    queryKey: qualityMetricsKeys.orgList(includeHidden),
    queryFn: ({ signal }) =>
      qualityMetricRqServiceApi.getOrganizationMetrics({ includeHidden, signal }),
    ...options,
  });
}

/**
 * Users tracked by a metric. Root-only. `placeholderData` keeps the previous
 * page on screen while the next one loads, so paging does not blank the table.
 */
export function useMetricUsers(
  id: string | null | undefined,
  { page = 1, pageSize = 50 }: { page?: number; pageSize?: number } = {},
  options: QueryOpts<MetricUsersPage> = {},
) {
  return useQuery<MetricUsersPage>({
    queryKey: qualityMetricsKeys.users(id, page, pageSize),
    queryFn: ({ signal }) =>
      qualityMetricRqServiceApi.getMetricUsers(id as string, { page, pageSize, signal }),
    enabled: !!id,
    placeholderData: previous => previous,
    ...options,
  });
}

/**
 * Trends for up to {@link MAX_COMPARED_USERS} users on one metric. The ids are
 * sorted into the query key so re-picking the same people in a different order
 * is a cache hit rather than a new request.
 */
export function useMetricUsersTrends(
  id: string | null | undefined,
  userIds: string[],
  options: QueryOpts<MetricUserTrends[]> = {},
) {
  return useQuery<MetricUserTrends[]>({
    queryKey: qualityMetricsKeys.usersTrends(id, userIds),
    queryFn: ({ signal }) =>
      qualityMetricRqServiceApi.getMetricUsersTrends(id as string, userIds, { signal }),
    enabled: !!id && userIds.length > 0 && userIds.length <= MAX_COMPARED_USERS,
    ...options,
  });
}

/** The largest page the cases endpoint serves, so this is the fewest requests. */
const CASES_FETCH_SIZE = 500;

/**
 * Every one of a user's cases on a metric, in one list. Root-only; the
 * Viewer-level cases endpoint is scoped to the caller, so this is the only way
 * to see someone else's.
 *
 * The endpoint pages, but the table does not: it filters, sorts and paginates
 * the whole set client-side, and a server page underneath would make its row
 * count and its paging disagree with the totals. So every page is fetched up
 * front — one request for the first 500 cases, and the rest in parallel once
 * `totalPages` says how many there are.
 *
 * @param id - The metric. The query stays disabled while this is nullish.
 * @param userId - The user whose cases these are. Likewise.
 * @param options - Extra react-query options, merged last.
 * @returns The query. `data.cases` holds every case, and `data.total` /
 *   `data.totalCompliant` are the server's own counts for them.
 * @example
 * const { data, isLoading } = useMetricUserCases(metricId, userId);
 */
export function useMetricUserCases(
  id: string | null | undefined,
  userId: string | null | undefined,
  options: QueryOpts<MetricCasesPage> = {},
) {
  return useQuery<MetricCasesPage>({
    queryKey: qualityMetricsKeys.userCases(id, userId),
    queryFn: async ({ signal }) => {
      const read = (page: number) =>
        qualityMetricRqServiceApi.getMetricUserCases(id as string, userId as string, {
          page,
          pageSize: CASES_FETCH_SIZE,
          signal,
        });

      const first = await read(1);
      const pages = first.totalPages ?? 1;
      if (pages <= 1) return first;

      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, index) => read(index + 2)),
      );
      return {
        ...first,
        hasMore: false,
        cases: rest.reduce((all, next) => all.concat(next.cases ?? []), first.cases ?? []),
      };
    },
    enabled: !!id && !!userId,
    ...options,
  });
}

/**
 * One case by its `externalId`, across every metric in the organization —
 * Viewer-level, so it covers cases the caller had no part in. Backs the case
 * detail in the management screen's user-cases modal.
 */
export function useQualityMetricCase(
  caseId: string | null | undefined,
  options: QueryOpts<QualityMetricCase> = {},
) {
  return useQuery<QualityMetricCase>({
    queryKey: qualityMetricsKeys.case(caseId),
    queryFn: ({ signal }) => qualityMetricRqServiceApi.getCaseById(caseId as string, { signal }),
    enabled: !!caseId,
    ...options,
  });
}

/**
 * Provider names on the organization's orphan records. Under
 * `qualityMetricsKeys.all`, so a user save that sets a provider name (which
 * attributes those records) refreshes it through the registry prefix.
 *
 * @param options - Extra react-query options, merged last.
 * @returns The query; `data` is most records first.
 * @example
 * const { data: providers = [], isLoading } = useUnmatchedProviders();
 */
export function useUnmatchedProviders(options: QueryOpts<UnmatchedProvider[]> = {}) {
  return useQuery<UnmatchedProvider[]>({
    queryKey: qualityMetricsKeys.unmatchedProviders(),
    queryFn: ({ signal }) => qualityMetricRqServiceApi.getUnmatchedProviders({ signal }),
    ...options,
  });
}

/**
 * Its own root key, deliberately outside `qualityMetricsKeys`: the metric
 * mutations invalidate that whole tree, and nothing a metric write does can
 * change the organization's configuration.
 */
export const qualityMetricConfigurationKeys = {
  all: ['qualityMetricConfiguration'] as const,
  detail: (organizationId: string | null | undefined) =>
    [...qualityMetricConfigurationKeys.all, organizationId] as const,
};

/**
 * The session organization's ingestion configuration, read here for its
 * default mapper — what a metric's `pull.mapper: null` resolves to. Root only,
 * like the screen that uses it. The API takes the organization from the
 * session; the key still carries its id, so a switch never serves the previous
 * organization's document.
 *
 * It takes the client's default stale time rather than `Infinity`: it is
 * written from a different app (back-office) with its own query client, so
 * there is no invalidation path between the two, and caching it for the whole
 * session would pin a stale default parser on an open screen indefinitely.
 *
 * @param options - Extra react-query options, merged last.
 * @returns The query. `data` is `null` when the organization has no
 *   configuration yet, which means no default parser — so a pull-enabled
 *   metric without its own is skipped by the sync service. Use `isSuccess` to
 *   tell that confirmed absence from a read still in flight.
 * @example
 * const { data: configuration, isSuccess } = useQualityMetricConfiguration();
 */
export function useQualityMetricConfiguration(
  options: QueryOpts<QualityMetricOrganizationConfiguration | null> = {},
) {
  const organizationId: string | null | undefined = useSelector(getOrganizationId);
  return useQuery<QualityMetricOrganizationConfiguration | null>({
    queryKey: qualityMetricConfigurationKeys.detail(organizationId),
    queryFn: ({ signal }) => qualityMetricRqServiceApi.getOrganizationConfiguration({ signal }),
    // No session organization yet means nothing to scope the read to.
    enabled: !!organizationId,
    ...options,
  });
}

export function useCreateQualityMetric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQualityMetricPayload) =>
      qualityMetricRqServiceApi.createQualityMetric(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qualityMetricsKeys.all }),
  });
}

export function useUpdateQualityMetric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: UpdateQualityMetricArgs) =>
      qualityMetricRqServiceApi.updateQualityMetric(args),
    onSuccess: () => qc.invalidateQueries({ queryKey: qualityMetricsKeys.all }),
  });
}
